/**
 * ProgressPanel —— PikeRag v2 SSE 推理/检索/工具过程面板
 *
 * 把后端的事件（reasoning / tool）翻译成"普通使用者能看懂"的进度描述：
 *   - 不暴露 trace_id / round / workflow 这类技术字段
 *   - 把 event/status 这类英文标识翻成中文短语
 *   - 检索结果会列出文档名称而不是 JSON
 */
import {
  BulbOutlined,
  CaretRightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  LoadingOutlined,
  ProfileOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { Collapse, Typography } from "antd";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatReasoningForDisplay, type FriendlyReasoningSection } from "./ProgressPanel.helpers";

export interface ProgressStep {
  kind: "reasoning" | "tool" | "status" | "progress" | "trace";
  event: string;
  phase?: string;
  status?: string;
  tool?: string;
  reasoning?: string;
  payload?: any;
  /** kind=status / progress：阶段/进度提示文本（V3 status.message / progress.message，后端已本地化） */
  message?: string;
  /** kind=trace：V3 trace.data（turn/role/toolName/toolArgs/toolResult/...） */
  trace?: any;
  raw?: any;
  createdAt: number;
  updatedAt: number;
}

interface ProgressPanelProps {
  steps: ProgressStep[];
  /** 是否处于流式阶段（展示旋转指示器） */
  streaming: boolean;
  /** 是否默认展开 */
  defaultOpen?: boolean;
}

/** 单步骤经过加工后的展示描述 */
interface FriendlyStep {
  /** 步骤左侧图标（缺省时回退到时间线圆点） */
  icon?: React.ReactNode;
  /** 一行短标题 */
  title: string;
  /** 副标题/详细一句话说明（可选） */
  description?: string;
  /** 工具调用参数：友好的键值对（label：value） */
  params?: { label: string; value: string }[];
  /** 工具调用结果条目：文档名 + 可选片段 */
  results?: { name?: string; snippet?: string }[];
  /** 结果条目总数（用于在列表底部提示“等共 N 项”） */
  resultsTotal?: number;
  /** 检索到的文档名列表等可枚举要点 */
  highlights?: string[];
  /** 模型的思考片段（仅 reasoning 类，普通文本） */
  reasoning?: string;
  /** 模型的思考片段（仅 reasoning 类，结构化字段） */
  reasoningSections?: FriendlyReasoningSection[];
}

const formatDuration = (ms: unknown, t: TFunction): string => {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1000) return t("opsmate.progress.duration.ms", { n });
  return t("opsmate.progress.duration.s", { n: (n / 1000).toFixed(1) });
};

const truncate = (text: string, max = 40): string => {
  if (typeof text !== "string") return "";
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

const uniq = <T,>(arr: T[]): T[] => Array.from(new Set(arr));

/**
 * 把纯文本里的 [N] / [N-M] 引用标记渲染成非交互的圆点角标。
 * 过程面板里的文本没有可点击的文档映射，统一渲染为 raw 圆点。
 */
const renderInlineCitations = (text: string): React.ReactNode => {
  if (typeof text !== "string" || !text) return text;
  const pattern = /\[(\d+(?:-\d+)?)\]/g;
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  let m: RegExpExecArray | null = pattern.exec(text);
  while (m !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const label = m[1].includes("-") ? m[1].split("-")[0] : m[1];
    nodes.push(
      <span key={`cite-${i++}`} className="opsmate-cite">
        {label}
      </span>,
    );
    last = m.index + m[0].length;
    m = pattern.exec(text);
  }
  if (nodes.length === 0) return text;
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const isFailedStatus = (s?: string): boolean => {
  const v = (s || "").toLowerCase();
  return v === "failed" || v === "error" || v === "failure";
};

const collectPlannerQueries = (payload: any): string[] => {
  if (Array.isArray(payload?.queries)) {
    return payload.queries
      .map((q: any) => (typeof q === "string" ? q : typeof q?.query === "string" ? q.query : ""))
      .filter((s: string) => !!s);
  }

  if (typeof payload?.query === "string") {
    return [payload.query];
  }

  return [];
};

/** 拥有专属友好名称/图标的已知工具；其余工具统一以“工具调用”泛化展示，不暴露原始标识 */
const KNOWN_TOOLS = ["load_skill", "kb_search", "kb_find_documents", "kb_read", "kb_get_outline"];

/** V3 工具名 → 友好短语（未知工具回退到泛化的“工具调用”，不展示 kb_get_outline 等原始标识） */
const friendlyToolName = (tool: string, t: TFunction): string => {
  if (KNOWN_TOOLS.includes(tool)) return t(`opsmate.progress.trace.tool.${tool}`);
  return t("opsmate.progress.trace.tool.default");
};

/** 从 V3 tool_result 中尽量抽取文档文件名，作为可枚举要点展示 */
const collectTraceDocNames = (toolResult: any): string[] => {
  if (!toolResult || typeof toolResult !== "object") return [];
  const names: string[] = [];
  const data = toolResult.data ?? toolResult;
  const byFilename = data?.by_filename;
  if (Array.isArray(byFilename)) {
    byFilename.forEach((d: any) => {
      const n = typeof d?.filename === "string" ? d.filename : "";
      if (n) names.push(n);
    });
  }
  const results = data?.results ?? (Array.isArray(data) ? data : undefined);
  if (Array.isArray(results)) {
    results.forEach((r: any) => {
      const n =
        typeof r?.filename === "string"
          ? r.filename
          : typeof r?.document_name === "string"
            ? r.document_name
            : typeof r?.documentName === "string"
              ? r.documentName
              : "";
      if (n) names.push(n);
    });
  }
  return uniq(names);
};

/** V3 工具名 → 图标 */
const TOOL_ICONS: Record<string, React.ReactNode> = {
  load_skill: <ThunderboltOutlined />,
  kb_search: <SearchOutlined />,
  kb_find_documents: <FileSearchOutlined />,
  kb_read: <FileTextOutlined />,
  kb_get_outline: <ProfileOutlined />,
};

/** 已知技能标识 → 友好中文短语的翻译键 */
const KNOWN_SKILL_KEYS: Record<string, string> = {
  "mvr-knowledge-qa": "opsmate.progress.trace.skill.knowledgeQa",
  "mvr-replan": "opsmate.progress.trace.skill.replan",
  "mvr-grounded-answer": "opsmate.progress.trace.skill.groundedAnswer",
};

/** load_skill 的技能标识翻成友好名称（未知技能回退到原标识） */
const friendlySkillName = (skill: string, t: TFunction): string => {
  const key = KNOWN_SKILL_KEYS[skill];
  return key ? t(key) : skill;
};

/** 工具参数键 → 友好标签的翻译键 */
const ARG_LABEL_KEYS: Record<string, string> = {
  query: "opsmate.progress.trace.arg.query",
  keyword: "opsmate.progress.trace.arg.keyword",
  keywords: "opsmate.progress.trace.arg.keyword",
  topK: "opsmate.progress.trace.arg.topK",
  top_k: "opsmate.progress.trace.arg.topK",
  documentIds: "opsmate.progress.trace.arg.documentIds",
  document_ids: "opsmate.progress.trace.arg.documentIds",
  documentId: "opsmate.progress.trace.arg.documentId",
  document_id: "opsmate.progress.trace.arg.documentId",
  sectionId: "opsmate.progress.trace.arg.sectionId",
  section_id: "opsmate.progress.trace.arg.sectionId",
  skillName: "opsmate.progress.trace.arg.skillName",
};

/** 把 snake_case / camelCase 参数键美化成可读标签 */
const prettifyArgKey = (key: string): string =>
  key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const friendlyArgLabel = (key: string, t: TFunction): string => {
  const k = ARG_LABEL_KEYS[key];
  return k ? t(k) : t("opsmate.progress.trace.arg.default", { label: prettifyArgKey(key) });
};

/** 把单个参数值格式化成友好字符串（数组/布尔/对象/通配符等） */
const formatArgValue = (value: any, t: TFunction): string => {
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    if (value.some((v) => v === "*")) return t("opsmate.progress.trace.arg.allDocs");
    const shown = value.slice(0, 3).map((v) => truncate(String(v), 24));
    let s = shown.join(t("opsmate.progress.queryJoiner"));
    if (value.length > 3) s += t("opsmate.progress.trace.arg.moreItems", { count: value.length });
    return s;
  }
  if (typeof value === "boolean") {
    return value ? t("opsmate.progress.boolean.yes") : t("opsmate.progress.boolean.no");
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "object" && value !== null) {
    try {
      return truncate(JSON.stringify(value), 80);
    } catch {
      return "";
    }
  }
  return truncate(String(value), 80);
};

/** 把 tool_call 的 toolArgs 整理成友好的键值对列表（最多 6 项） */
const collectTraceArgs = (toolArgs: any, t: TFunction): { label: string; value: string }[] => {
  if (!toolArgs || typeof toolArgs !== "object") return [];
  const out: { label: string; value: string }[] = [];
  for (const [k, v] of Object.entries(toolArgs)) {
    if (v === null || v === undefined || v === "") continue;
    const value = formatArgValue(v, t);
    if (!value) continue;
    out.push({ label: friendlyArgLabel(k, t), value });
    if (out.length >= 6) break;
  }
  return out;
};

/** 从 tool_result 推断命中数量：优先 resultCount，其次 total_unique_documents，最后文档名数量 */
const extractTraceCount = (trace: any, docNames: string[]): number => {
  const rc = Number(trace?.resultCount);
  if (Number.isFinite(rc) && rc > 0) return rc;
  const tr = trace?.toolResult;
  const data = tr && typeof tr === "object" ? (tr.data ?? tr) : null;
  const total = Number((data as any)?.total_unique_documents);
  if (Number.isFinite(total)) return total;
  return docNames.length;
};

/** 判断 tool_result 是否为“空结果”（null / success=false / total=0 / 无任何命中） */
const isEmptyTraceResult = (toolResult: any, count: number): boolean => {
  if (count > 0) return false;
  if (toolResult == null) return true;
  if (typeof toolResult === "object") {
    if (toolResult.success === false) return true;
    const data = toolResult.data ?? toolResult;
    const total = Number((data as any)?.total_unique_documents);
    if (Number.isFinite(total)) return total === 0;
  }
  return true;
};

/** kind=trace：把 V3 Agent 执行轨迹翻译成友好描述 */
const describeTraceStep = (step: ProgressStep, t: TFunction): FriendlyStep => {
  const trace = step.trace && typeof step.trace === "object" ? step.trace : {};
  const role = String(trace.role ?? "");
  const tool = String(trace.toolName ?? "");
  const isKnownTool = KNOWN_TOOLS.includes(tool);
  const friendlyTool = friendlyToolName(tool, t);
  const toolIcon = TOOL_ICONS[tool];
  // 已知工具用“正在执行：xxx / xxx 已完成”包装；未知工具直接用泛化的“工具调用”作为标题（不带前缀）
  const callTitle = isKnownTool
    ? t("opsmate.progress.trace.call.title", { tool: friendlyTool })
    : friendlyTool;
  // tool_result 不暴露 toolName，统一用通用的“检索完成”标题（命中数/用时/文档名仍照常展示）
  const resultTitle = t("opsmate.progress.retrieval.completed.title");

  // assistant / final：阶段性文本输出
  if ((role === "assistant" || role === "final") && typeof trace.content === "string" && trace.content) {
    return {
      icon: <BulbOutlined />,
      title: t("opsmate.progress.trace.assistant"),
      description: truncate(trace.content, 120),
    };
  }

  if (role === "tool_call") {
    // load_skill：单独展示“正在加载技能 + 友好技能名”，不暴露 mvr-xxx 标识
    if (tool === "load_skill") {
      const skill = typeof trace.toolArgs?.skillName === "string" ? trace.toolArgs.skillName : "";
      return {
        icon: <ThunderboltOutlined />,
        title: t("opsmate.progress.trace.loadSkill.call"),
        description: skill ? friendlySkillName(skill, t) : undefined,
      };
    }

    const args = trace.toolArgs && typeof trace.toolArgs === "object" ? trace.toolArgs : {};
    return {
      icon: toolIcon ?? <ToolOutlined />,
      title: callTitle,
      params: collectTraceArgs(args, t),
    };
  }

  if (role === "tool_result") {
    // load_skill 结果只是“指令已加载”，给一个明确的就绪提示即可
    if (tool === "load_skill") {
      return {
        icon: <CheckCircleOutlined />,
        title: t("opsmate.progress.trace.loadSkill.done"),
      };
    }

    // 纯文本结果（如 kb_get_outline 返回文档大纲文本）：直接截断展示正文，避免被误判为“空结果”
    if (typeof trace.toolResult === "string") {
      const text = trace.toolResult.trim();
      if (text) {
        return {
          icon: <CheckCircleOutlined />,
          title: resultTitle,
          description: truncate(text.replace(/\s+/g, " "), 200),
        };
      }
    }

    const docNames = collectTraceDocNames(trace.toolResult);
    const count = extractTraceCount(trace, docNames);

    // 空结果：明确告知“没找到”，而不是只剩一个孤零零的标题
    if (isEmptyTraceResult(trace.toolResult, count)) {
      return {
        icon: <ExclamationCircleOutlined />,
        title: resultTitle,
        description:
          tool === "kb_find_documents"
            ? t("opsmate.progress.trace.result.emptyDocs")
            : t("opsmate.progress.trace.result.empty"),
      };
    }

    // 命中的文档名（去重）由渲染层合并进上一条“检索中”区块；不再单独展示标题/命中数/用时
    return {
      title: resultTitle,
      highlights: docNames,
    };
  }

  // 其它 role 兜底
  return {
    icon: toolIcon ?? <ToolOutlined />,
    title: tool ? callTitle : t("opsmate.progress.fallback.tool"),
  };
};

const describeStep = (step: ProgressStep, t: TFunction): FriendlyStep => {
  // ── 阶段提示（V3 status）：映射成前端当前语言后再作为标题 ──
  if (step.kind === "status") {
    // 不指定图标，渲染层回退到通用时间线圆点（opsmate-progress-dot）
    return {
      title: step.message || "",
    };
  }

  // ── 进度提示（V3 progress）：上游已本地化文本，直接作为标题展示 ──
  if (step.kind === "progress") {
    return {
      icon: <ThunderboltOutlined />,
      title: step.message?.trim() || t("opsmate.progress.reasoning.thinking"),
    };
  }

  // ── Agent 执行轨迹（V3 trace）──
  if (step.kind === "trace") {
    return describeTraceStep(step, t);
  }

  const event = (step.event || "").trim();
  const payload = step.payload && typeof step.payload === "object" ? step.payload : {};
  const failed = isFailedStatus(step.status);

  // ── 规划/决策 ──
  if (event === "planner.retrieve_decision.completed") {
    const need =
      (payload as any).continue_retrieve ??
      (payload as any).need_retrieval ??
      (payload as any).needRetrieval;
    return {
      title: t("opsmate.progress.planner.decision.title"),
      description:
        need === true
          ? t("opsmate.progress.planner.decision.need")
          : need === false
            ? t("opsmate.progress.planner.decision.noNeed")
            : t("opsmate.progress.planner.decision.done"),
      ...formatReasoningForDisplay(typeof step.reasoning === "string" ? step.reasoning : "", t),
    };
  }

  if (event === "planner.query_proposal.completed" || event === "query.proposed") {
    const queries = collectPlannerQueries(payload);

    return {
      title:
        queries.length > 1
          ? t("opsmate.progress.planner.queryProposal.titleMulti")
          : t("opsmate.progress.planner.queryProposal.titleSingle"),
      description:
        queries.length > 0
          ? t("opsmate.progress.planner.queryProposal.keywords", {
              queries: queries.join(t("opsmate.progress.queryJoiner")),
            })
          : undefined,
      ...formatReasoningForDisplay(typeof step.reasoning === "string" ? step.reasoning : "", t),
    };
  }

  // ── 推理（LLM 思考）──
  if (step.kind === "reasoning" || event.startsWith("llm.")) {
    return {
      icon: failed ? <ExclamationCircleOutlined /> : <BulbOutlined />,
      title: failed
        ? t("opsmate.progress.reasoning.failed")
        : t("opsmate.progress.reasoning.thinking"),
      ...formatReasoningForDisplay(typeof step.reasoning === "string" ? step.reasoning : "", t),
    };
  }

  // ── 检索流程 ──
  if (event === "retrieval.started") {
    const query = typeof (payload as any).query === "string" ? (payload as any).query : "";
    return {
      title: t("opsmate.progress.retrieval.started.title"),
      description: query
        ? t("opsmate.progress.retrieval.started.query", { query: truncate(query, 80) })
        : undefined,
    };
  }

  if (event === "retrieval.completed") {
    if (failed) {
      return {
        title: t("opsmate.progress.retrieval.failed.title"),
        description: t("opsmate.progress.retrieval.failed.desc"),
      };
    }
    const newly = Number((payload as any).newly_added_count ?? NaN);
    const total = Number((payload as any).retrieval_count ?? NaN);
    const dur = formatDuration((payload as any).duration_ms, t);
    const parts: string[] = [];
    if (Number.isFinite(newly) && newly > 0)
      parts.push(t("opsmate.progress.retrieval.foundNew", { count: newly }));
    else if (Number.isFinite(total) && total > 0)
      parts.push(t("opsmate.progress.retrieval.foundTotal", { count: total }));
    if (dur) parts.push(t("opsmate.progress.retrieval.duration", { duration: dur }));
    return {
      title: t("opsmate.progress.retrieval.completed.title"),
      description: parts.length > 0 ? parts.join(t("opsmate.progress.partSeparator")) : undefined,
    };
  }

  if (event === "retrieval.results") {
    const results: any[] = Array.isArray((payload as any).results_preview)
      ? (payload as any).results_preview
      : [];
    const count = Number((payload as any).result_count ?? results.length) || results.length;
    const docNames = uniq(
      results
        .map((r: any) =>
          typeof r?.filename === "string"
            ? r.filename
            : typeof r?.document_name === "string"
              ? r.document_name
              : "",
        )
        .filter((s: string) => !!s),
    );
    return {
      title:
        count > 0
          ? t("opsmate.progress.retrieval.results.title", { count })
          : t("opsmate.progress.retrieval.results.titleEmpty"),
      description:
        docNames.length > 0
          ? t("opsmate.progress.retrieval.results.fromDocs", { count: docNames.length })
          : undefined,
      highlights: docNames,
    };
  }

  // ── 兜底（未识别事件）：用一段友好文案而不是裸 event ──
  return {
    title: failed
      ? t("opsmate.progress.fallback.failed")
      : step.kind === "tool"
        ? t("opsmate.progress.fallback.tool")
        : t("opsmate.progress.fallback.reasoningStep"),
    ...formatReasoningForDisplay(typeof step.reasoning === "string" ? step.reasoning : "", t),
  };
};

const ProgressPanel: React.FC<ProgressPanelProps> = ({ steps, streaming, defaultOpen = false }) => {
  const { t } = useTranslation();
  const hasSteps = Array.isArray(steps) && steps.length > 0;

  const activeKeys = useMemo(() => {
    if (streaming) return ["progress"];
    return defaultOpen ? ["progress"] : [];
  }, [streaming, defaultOpen]);

  // 把 event=trace 且 role=tool_result 的内容合并进上一条 event=status 区块：
  // 命中的文档名（及文本类结果描述）挂到上一条“检索中”区块下，tool_result 不再单独成行。
  const visibleSteps = useMemo(() => {
    const items: { step: ProgressStep; friendly: FriendlyStep }[] = [];
    let lastStatusIdx = -1;
    for (const step of steps) {
      const friendly = describeStep(step, t);
      const isToolResult =
        step.kind === "trace" && String(step.trace?.role ?? "") === "tool_result";
      if (isToolResult && lastStatusIdx >= 0) {
        const target = items[lastStatusIdx];
        target.friendly = {
          ...target.friendly,
          highlights: uniq([
            ...(target.friendly.highlights ?? []),
            ...(friendly.highlights ?? []),
          ]),
          description: target.friendly.description ?? friendly.description,
        };
        continue;
      }
      if (step.kind === "status") lastStatusIdx = items.length;
      items.push({ step, friendly });
    }
    return items;
  }, [steps, t]);

  if (!hasSteps && !streaming) return null;

  const headerLabel = streaming
    ? hasSteps
      ? t("opsmate.progress.headerThinkingWithSteps", { count: steps.length })
      : t("opsmate.progress.headerThinking")
    : t("opsmate.progress.headerCompleted");

  return (
    <Collapse
      size="small"
      ghost
      bordered={false}
      defaultActiveKey={activeKeys}
      expandIcon={({ isActive }) => (
        <CaretRightOutlined rotate={isActive ? 90 : 0} style={{ fontSize: 12 }} />
      )}
      items={[
        {
          key: "progress",
          label: (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "rgba(0,0,0,0.65)",
                fontSize: 13,
              }}
            >
              {streaming ? (
                <LoadingOutlined spin style={{ fontSize: 12, color: "#830051" }} />
              ) : null}
              <span>{headerLabel}</span>
            </span>
          ),
          children: (
            <div className="opsmate-progress-list">
              {visibleSteps.map(({ step, friendly }, idx) => {
                const isLast = idx === visibleSteps.length - 1;
                const active = streaming && isLast;
                const failed = isFailedStatus(step.status);
                return (
                  <div
                    key={`${step.createdAt}-${idx}`}
                    className={`opsmate-progress-item${active ? " is-active" : ""}${failed ? " is-failed" : ""}`}
                  >
                    {friendly.icon ? (
                      <span className="opsmate-progress-icon" aria-hidden>
                        {active ? <LoadingOutlined spin /> : friendly.icon}
                      </span>
                    ) : (
                      <span className="opsmate-progress-dot" aria-hidden />
                    )}

                    <div className="opsmate-progress-body">
                      <div className="opsmate-progress-title">{friendly.title}</div>
                      {friendly.description ? (
                        <Typography.Text
                          type="secondary"
                          style={{
                            fontSize: 14,
                            display: "block",
                            color: "rgba(0,0,0,0.65)",
                            marginTop: 2,
                          }}
                        >
                          {renderInlineCitations(friendly.description)}
                        </Typography.Text>
                      ) : null}
                      {friendly.params && friendly.params.length > 0 ? (
                        <ul className="opsmate-progress-params">
                          {friendly.params.map((p, i) => (
                            <li key={`${p.label}-${i}`}>
                              <span className="k">{p.label}</span>
                              <span className="v" title={p.value}>
                                {p.value}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : null}
                      {friendly.results && friendly.results.length > 0 ? (
                        <ul className="opsmate-progress-results">
                          {friendly.results.map((r, i) => (
                            <li key={`${r.name ?? ""}-${i}`}>
                              {r.name ? (
                                <div className="name" title={r.name}>
                                  {r.name}
                                </div>
                              ) : null}
                              {r.snippet ? (
                                <div className="snippet">{renderInlineCitations(r.snippet)}</div>
                              ) : null}
                            </li>
                          ))}
                          {friendly.resultsTotal && friendly.resultsTotal > friendly.results.length ? (
                            <li className="more">
                              {t("opsmate.progress.moreDocuments", {
                                count: friendly.resultsTotal,
                              })}
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                      {friendly.highlights && friendly.highlights.length > 0 ? (
                        <ul className="opsmate-progress-highlights">
                          {friendly.highlights.slice(0, 6).map((name, i) => (
                            <li key={`${name}-${i}`} title={name}>
                              <FileTextOutlined className="doc-icon" aria-hidden />
                              <span className="doc-name">{name}</span>
                            </li>
                          ))}
                          {friendly.highlights.length > 6 ? (
                            <li className="more">
                              {t("opsmate.progress.moreDocuments", {
                                count: friendly.highlights.length,
                              })}
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                      {friendly.reasoning ? (
                        <div className="opsmate-progress-reasoning">
                          {renderInlineCitations(friendly.reasoning)}
                        </div>
                      ) : null}
                      {friendly.reasoningSections && friendly.reasoningSections.length > 0 ? (
                        <div className="opsmate-progress-reasoning">
                          {friendly.reasoningSections.map((section, sectionIdx) => (
                            <div
                              className="opsmate-progress-reasoning-section"
                              key={`${section.title}-${sectionIdx}`}
                            >
                              <div className="opsmate-progress-reasoning-section-title">
                                {section.title}
                              </div>
                              <div className="opsmate-progress-reasoning-section-content">
                                {renderInlineCitations(section.content)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
              {streaming && steps.length === 0 ? (
                <div
                  style={{
                    fontSize: 12,
                    color: "rgba(0,0,0,0.45)",
                    paddingInline: 16,
                  }}
                >
                  {t("opsmate.progress.analyzing")}
                </div>
              ) : null}
            </div>
          ),
        },
      ]}
    />
  );
};

export default ProgressPanel;
